import type { LogicConfig, LogicResult, LogicName } from '../types';
import { processSkipLogic } from '../core/skipLogic';
import { processResultLogic } from '../core/resultLogic';

class LogicProcessor {
  private processors: Map<LogicName, (config: LogicConfig, value: string | string[] | null) => Promise<LogicResult>>;

  constructor() {
    this.processors = new Map();
  }

  /**
   * Register default processors
   */
  registerDefaultProcessors(): void {
    this.registerProcessor('skipLogic', processSkipLogic);
    this.registerProcessor('resultLogic', processResultLogic);
  }

  /**
   * Register a logic processor for a specific logic type
   */
  registerProcessor(
    name: LogicName,
    processor: (config: LogicConfig, value: string | string[] | null) => Promise<LogicResult>
  ) {
    this.processors.set(name, processor);
  }

  /**
   * Process a list of logic configurations with a given value
   */
  async processLogic(
    logicConfigs: LogicConfig[],
    value: string | string[] | null
  ): Promise<LogicResult[]> {
    const results: LogicResult[] = [];

    for (const logic of logicConfigs) {
      const processor = this.processors.get(logic.name);
      if (!processor) {
        continue;
      }

      try {
        // Pass the entire logic config to maintain all necessary data
        const result = await processor(logic, value);
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          nextAction: {
            type: 'standard',
            value: 0
          },
          metadata: {
            error: process.env.NODE_ENV === 'development' ? error : 'Logic processing error'
          }
        });
      }
    }

    return results;
  }

  /**
   * Get the next action from the logic results
   * This determines what should happen next (skip, show result, etc.)
   */
  getNextAction(results: LogicResult[]): LogicResult['nextAction'] | null {
    // Priority order: show_result > skip > update_score
    const priorityOrder = ['show_result', 'skip', 'update_score'] as const;

    for (const actionType of priorityOrder) {
      const result = results.find(r => r.nextAction?.type === actionType);
      if (result?.nextAction) {
        return result.nextAction;
      }
    }

    return null;
  }
}

// Create and export a singleton instance with default processors
const logicProcessor = new LogicProcessor();
logicProcessor.registerDefaultProcessors();
export { logicProcessor };

// Export the class for testing purposes
export { LogicProcessor }; 